import React, { useCallback, useEffect, useState } from 'react';

/**
 * Article hero image.
 *  - has url, loading -> skeleton shimmer (space reserved via 16:9 aspect ratio)
 *  - has url, loaded  -> the R2 image (object-cover in a 16:9 box). Current source
 *    images are square, so top/bottom is trimmed; once the admin generates 16:9
 *    images this box shows them in full with no crop.
 *  - no url, or load error -> renders nothing at all (no placeholder, no reserved space)
 *
 * Most articles going forward have an image; when one doesn't (older articles,
 * or a rare R2 hiccup) we simply omit the image slot rather than show a filler.
 *
 * Hero images are 1024x1024 PNGs (~1.5-2MB) on Cloudflare R2 with a long
 * Cache-Control, so we lazy-load them and reserve layout space while loading to
 * avoid content shift.
 */

type Variant = 'card' | 'hero';

interface HeroImageProps {
  url?: string | null;
  alt: string;
  variant?: Variant;
  className?: string;
  /** Load straight away rather than waiting for the image to scroll into
   *  view. Set on the first few cards: after changing a filter the feed is
   *  rebuilt, and lazy images just below the fold left visible gaps that
   *  read as missing pictures until the student scrolled to them. */
  priority?: boolean;
}

const MAX_RETRIES = 2;

// How far ahead of the viewport to start fetching. Chrome's own lazy loading
// looks roughly this far ahead, but WKWebView on iOS waits until an image is
// nearly on screen — which is why pictures appeared missing on iPhone while
// Android looked fine. Observing it ourselves makes both platforms behave the
// same.
const PRELOAD_MARGIN = '800px';

const HeroImage: React.FC<HeroImageProps> = ({ url, alt, variant = 'card', className = '', priority = false }) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [shouldLoad, setShouldLoad] = useState(priority);
  // A callback ref rather than useRef: this is state, so the observer effect
  // re-runs once the element actually mounts. With a plain ref the effect
  // could run while the ref was still null, bail out, and never try again —
  // leaving that card with no image for the rest of the session.
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const setBoxRef = useCallback((n: HTMLDivElement | null) => setBox(n), []);

  // Reset when the source changes (e.g. card reused across virtualized lists).
  // Deliberately keyed on `url` alone — see the effect below for why `priority`
  // must not be in here.
  useEffect(() => {
    setStatus('loading');
    setAttempt(0);
    setShouldLoad(priority);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // `priority` is `index < 4`, so filtering by category moves a card up the
  // grid and flips it on for an image that is already loaded. Cards are keyed
  // by article id and a filtered list is a subset of the same articles, so
  // React reuses the component rather than remounting it.
  //
  // This may therefore only ever START loading. Resetting `status` to
  // 'loading' here used to strand the card on its grey shimmer for good: the
  // <img> element and its src are unchanged, so the browser fires no second
  // load event, `onLoad` never runs again, and the fully-decoded image sits
  // underneath at opacity 0. That is why a picture could be missing on the
  // card and perfect in the article, which mounts its own HeroImage.
  useEffect(() => {
    if (priority) setShouldLoad(true);
  }, [priority]);

  // Start fetching once the card is within PRELOAD_MARGIN of the viewport.
  useEffect(() => {
    if (shouldLoad || !box) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true); // no observer support: just load it
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: PRELOAD_MARGIN }
    );
    io.observe(box);
    return () => io.disconnect();
  }, [shouldLoad, box, url]);

  // Backstop: load anyway shortly after mount. WKWebView defers observer
  // callbacks during momentum scrolling, so on iOS the trigger can simply
  // never arrive and the card sits empty. Correctness must not depend on the
  // observer firing; it is only there to save data when it does.
  useEffect(() => {
    if (shouldLoad) return;
    const t = setTimeout(() => setShouldLoad(true), 2500);
    return () => clearTimeout(t);
  }, [shouldLoad, url]);

  // Heroes are large PNGs, and a feed pulls a dozen at once. On mobile data a
  // few requests drop, and without a retry those articles rendered with no
  // image at all for the rest of the session. Retry a couple of times with a
  // short backoff before giving up.
  const handleError = () => {
    if (attempt < MAX_RETRIES) {
      const next = attempt + 1;
      setTimeout(() => {
        setAttempt(next);
        setStatus('loading');
      }, 400 * next);
    } else {
      setStatus('error');
    }
  };

  // No image, or it failed to load: render nothing (leave the slot blank).
  if (!url || status === 'error') return null;

  const rounded = variant === 'card' ? 'rounded-xl' : 'rounded-none';

  return (
    <div
      ref={setBoxRef}
      className={`relative w-full overflow-hidden bg-gray-100 ${rounded} ${className}`}
      style={{ aspectRatio: '16 / 9' }}
    >
      {/* Skeleton shimmer while the image loads */}
      {status === 'loading' && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-300" />
      )}
      {shouldLoad && (
      <img
        // The key forces a fresh element per attempt, and the query param
        // sidesteps a cached failed response, so a retry really re-fetches.
        key={attempt}
        src={attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`}
        alt={alt}
        loading="eager"
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        width={1024}
        height={576}
        onLoad={() => setStatus('loaded')}
        onError={handleError}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        }`}
      />
      )}
    </div>
  );
};

export default HeroImage;
